import {
  createProducRef,
  getProductRef,
  getProductFactoryRef,
  getProductSettingRef,
  getProductSupplierRef,
  getProductDetailRef,
  updateProductRef,
  deleteProductRef,
  getProductFieldPermissionRef,
} from './ProductService'
import type { IPagination } from '@/interface/common.interface'
import type {
  CreateProductPayload,
  ProductFilterPayload,
  ProductSearchParams,
  ProductSource,
  UpdateProductPayload,
} from './ProductService'
import type {
  ProductFieldAccess,
  ProductFieldPermissionRecord,
  ProductFieldPermissionTarget,
} from './ProductFieldPermission.types'
import { isProductManualSourceEnabled } from './ProductSetting.utils'
import {
  toProductDateFormValue,
  toProductDatePayloadValue,
} from './ProductDateValue'
import { v4 as uuidv4 } from 'uuid'
import { uploadInspectionTaskPhoto } from '@/screens/QSF/InspectionReport/InSpectionReport.service'
import { pick } from 'lodash'
import imageCompression from 'browser-image-compression'
import HttpRequest from '@/config/axios.config'

const productImageMaxSizeMB =
  Number(process.env.REACT_APP_PRODUCT_IMAGE_MAX_SIZE_MB) || 0.5
const productImageMaxWidthOrHeight =
  Number(process.env.REACT_APP_PRODUCT_IMAGE_MAX_WIDTH_OR_HEIGHT) || 1920

export type ProductMedia = {
  media_id?: string
  name?: string
  type?: string
  id?: string
  reference_id?: string | null
  inspection_id?: string | null
  table?: string
  status?: string
  size?: number | string
  url_path?: string
  thumbnail_url_path?: string
  option?: Record<string, unknown> | null
  created_date?: string
  last_modified_date?: string
}

export type ProductPropertyValue = {
  data_type?: string
  value?: string
  field?: string
}

export type Product = {
  id: string
  number?: string
  name?: string
  source?: ProductSource | string | null
  status?: string
  picture?: ProductMedia | string | null
  created_by?: {
    id?: string
    first_name?: string | null
    last_name?: string | null
    avatar?: ProductMedia | null
  } | null
  supplier?: {
    id?: string
    name?: string | null
  } | null
  factory?: {
    id?: string
    name?: string | null
  } | null
  property_values?: ProductPropertyValue[]
}

export type ProductFieldSetting = {
  field: string
  display_name: string
}

export type ProductSettingFieldDataType =
  | 'text'
  | 'number'
  | 'date'
  | 'file'
  | 'image'
  | 'image_description'
  | 'select'
  | 'textarea'

export type ProductDefaultFieldDescription = {
  name: string
  field: string
  required?: boolean
}

export type ProductDefaultFieldOption = {
  label: string
  value: string | number | null
  display_order?: number
}

export type ProductDefaultFieldSetting = {
  property_id?: string | null
  name: string
  field: string
  value: string
  source?: string
  required: boolean
  dynamic?: boolean
  data_type: ProductSettingFieldDataType
  data_source?: string
  description?: ProductDefaultFieldDescription
  display_order: number
  options?: ProductDefaultFieldOption[]
  system?: boolean
}

export type ProductSetting = {
  fieldNames: ProductFieldSetting[]
  defaultFields: ProductDefaultFieldSetting[]
  hasManualSource: boolean
  internalFields: ProductDefaultFieldSetting[]
}

export type ProductSupplier = {
  id: string
  code?: string | null
  name: string
  email?: string | null
  address?: string | null
  phone_number?: string | null
  contact_person_name?: string | null
  contact_person_phone_number?: string | null
  is_factory?: boolean
  factories?: ProductSupplier[]
  parent?: {
    id?: string
    name?: string
  } | null
  status?: string
  type?: string | null
  city?: string | null
  picture?: ProductMedia | string | null
  compliance_check?: unknown
  rating?: unknown
  remarks?: string | null
  created_date?: string
  last_modified_date?: string
}

export type ProductListResult = {
  products: Product[]
  pagination: IPagination | null
}

export type ProductDetailUser = {
  id: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  position?: string | null
  phone_number?: string | null
  status?: string
  created_date?: string
  last_modified_date?: string
  property_values?: unknown
  first_login?: string | null
  last_login?: string | null
  ip_address?: string | null
  avatar?: ProductMedia | null
  subscription?: {
    role_access?: {
      role?: string
    }
  } | null
  subscriptions?: Array<{
    role_access?: {
      role?: string
    }
  }>
  role?: string
  role_access?: {
    role?: string
  }
}

export type ProductDetailPropertyDescription = {
  name?: string
  field?: string
  value?: string | number | null
}

export type ProductDetailPropertyValue = {
  id: string
  table?: string
  reference_id?: string
  field: string
  value?: string | ProductMedia | Record<string, unknown> | null
  data_type?: ProductSettingFieldDataType | string
  description?: ProductDetailPropertyDescription | null
  option?: unknown
  is_default?: boolean
  created_date?: string
  last_modified_date?: string
  deletedAt?: string | null
  name?: string
}

export type ProductDetail = {
  id: string
  number?: string
  name?: string
  source?: ProductSource | string | null
  status?: string
  option?: unknown
  extra?: unknown
  created_date?: string
  last_modified_date?: string
  deletedAt?: string | null
  deleted_by?: string | null
  picture?: ProductMedia | string | null
  created_by?: ProductDetailUser | null
  last_modified_by?: ProductDetailUser | null
  supplier?: ProductSupplier | null
  factory?: ProductSupplier | null
  property_values?: ProductDetailPropertyValue[]
  subscription?: {
    role_access?: {
      role?: string
    }
  } | null
  subsciription?: {
    role_access?: {
      role?: string
    }
  } | null
}

export type ProductCreatePropertyDescription =
  ProductDefaultFieldDescription & {
    value?: string
  }

export type ProductCreatePropertyValue = {
  field: string
  name: string
  value: string | number | ProductMedia[]
  data_type: ProductSettingFieldDataType
  description?: ProductCreatePropertyDescription
  required: boolean
  display_order: number
}

export type ProductCreatePayload = CreateProductPayload
export type ProductUpdatePayload = UpdateProductPayload

type ProductFormValue = {
  picture?: ProductUploadFile[]
  number?: string
  name?: string
  supplier?: string
  [key: string]: any
}

type ProductUploadFile = ProductMedia & {
  uid?: string
  size?: number
  url?: string
  thumbUrl?: string
  preview?: string
  status?: string
  originFileObj?: File
  response?: any
}

const getBase64 = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })

function getCachedProductImageDataUrl(file: ProductUploadFile) {
  return [file.preview, file.thumbUrl].find(
    (url): url is string => typeof url === 'string' && url.startsWith('data:')
  )
}

// Reads the picked image into `file.preview` right at selection time, while
// the underlying file handle is still readable. On Android Chrome the handle
// can become unreadable by submit time (the content URI gets invalidated),
// so this cached copy is what uploadProductImage falls back to. Best-effort:
// a failed capture must never break the selection flow.
export async function cacheProductImagePreview(file: ProductUploadFile) {
  if (!file?.originFileObj || getCachedProductImageDataUrl(file)) return

  try {
    file.preview = await getBase64(file.originFileObj)
  } catch (error) {
    console.warn('Could not cache product image preview at selection.', error)
  }
}

function pickProductMedia(file?: ProductUploadFile): ProductMedia {
  const media = file?.response?.data ?? file?.response ?? file

  return pick(media, [
    'media_id',
    'name',
    'type',
    'id',
    'url_path',
    'thumbnail_url_path',
  ])
}

function parseJsonValue(value?: unknown, depth = 0): unknown {
  if (!value) return null
  if (typeof value !== 'string') return value

  try {
    const parsedValue = JSON.parse(value)

    if (typeof parsedValue === 'string' && depth < 3) {
      return parseJsonValue(parsedValue, depth + 1)
    }

    return parsedValue
  } catch {
    const unescapedValue = value.replace(/\\"/g, '"')
    const mediaObjectMatch = unescapedValue.match(
      /\{[^{}]*(media_id|url_path|thumbnail_url_path)[^{}]*\}/
    )

    if (mediaObjectMatch?.[0] && mediaObjectMatch[0] !== value) {
      return parseJsonValue(mediaObjectMatch[0], depth + 1)
    }

    return value
  }
}

export function parseProductMediaValue(
  value?:
    | string
    | ProductMedia[]
    | ProductMedia
    | Record<string, unknown>
    | null
): ProductMedia[] {
  if (!value) return []

  if (Array.isArray(value)) {
    return value.filter((media) => Object.keys(media ?? {}).length > 0)
  }

  if (typeof value === 'object') {
    return Object.keys(value).length ? [value as ProductMedia] : []
  }

  const parsedValue = parseJsonValue(value)

  if (Array.isArray(parsedValue)) {
    return parsedValue as ProductMedia[]
  }

  if (parsedValue && typeof parsedValue === 'object') {
    const objectValue = parsedValue as Record<string, unknown>

    if (!Object.keys(objectValue).length) {
      return []
    }

    if (objectValue.url_path || objectValue.thumbnail_url_path) {
      return [objectValue as ProductMedia]
    }

    const firstKey = Object.keys(objectValue)[0]
    const nestedValue = parseJsonValue(firstKey)

    if (
      nestedValue &&
      typeof nestedValue === 'object' &&
      Object.keys(nestedValue).length
    ) {
      return [nestedValue as ProductMedia]
    }
  }

  return []
}

function toProductUploadFile(media?: ProductMedia | string | null) {
  if (!media || typeof media === 'string') return undefined

  return {
    ...media,
    uid: media.id || media.media_id || media.name || uuidv4(),
    name: media.name || media.id || media.media_id || 'image',
    status: 'done',
    url: media.url_path,
    thumbUrl: media.thumbnail_url_path || media.url_path,
  } as ProductUploadFile
}

function getTextPropertyValue(propertyValue: ProductDetailPropertyValue) {
  const parsedValue = parseJsonValue(propertyValue.value)

  if (
    typeof parsedValue === 'string' ||
    typeof parsedValue === 'number' ||
    typeof parsedValue === 'boolean'
  ) {
    return parsedValue
  }

  return propertyValue.value ?? ''
}

async function uploadProductImage(
  file?: ProductUploadFile,
  referenceId?: string
) {
  if (!file) return null

  if (!file.originFileObj) {
    return pickProductMedia(file)
  }

  const uuidImage = uuidv4()
  let imageToUpload: File | Blob = file.originFileObj
  let dataRaw: string

  try {
    try {
      imageToUpload = await imageCompression(file.originFileObj, {
        maxSizeMB: productImageMaxSizeMB,
        maxWidthOrHeight: productImageMaxWidthOrHeight,
      })
    } catch (compressionError) {
      console.warn(
        'Product image compression failed; uploading original file.',
        compressionError
      )
    }

    dataRaw = await getBase64(imageToUpload)
  } catch (readError) {
    // On Android Chrome the picked file can become unreadable by submit time
    // (the content URI behind it gets invalidated). Upload the data URL
    // captured at selection instead of failing the whole product submission.
    const cachedDataUrl = getCachedProductImageDataUrl(file)
    if (!cachedDataUrl) throw readError

    console.warn(
      'Reading product image failed; uploading the preview captured at selection.',
      readError
    )
    dataRaw = cachedDataUrl
    imageToUpload = file.originFileObj
  }

  const payload = {
    media_id: uuidImage,
    reference_id: referenceId || uuidImage,
    table: 'products',
    name: uuidImage,
    data_raw: dataRaw,
    type: imageToUpload.type || 'image/jpg',
    size: `${imageToUpload.size || 0}`,
  }
  const uploadedImage = await uploadInspectionTaskPhoto(payload)

  return pickProductMedia(uploadedImage)
}

async function uploadProductImageList(
  fileList?: ProductUploadFile[],
  referenceId?: string
) {
  if (!fileList?.length) return []

  const uploadedImages: ProductMedia[] = []
  for (const file of fileList) {
    const uploadedImage = await uploadProductImage(file, referenceId)
    if (uploadedImage?.id || uploadedImage?.media_id) {
      uploadedImages.push(uploadedImage)
    }
  }

  return uploadedImages
}

// Tags an image-upload failure so the Product create/update handler can tell
// "the image upload failed" apart from "the create/update API call failed".
// The original error (including any axios `.response`) is preserved and rethrown.
function tagProductImageUploadError(error: any): never {
  if (error && typeof error === 'object') {
    error.isProductImageUpload = true
  }
  throw error
}

export async function prepareCreateProductPayload(
  formValue: ProductFormValue,
  fields: ProductDefaultFieldSetting[],
  getFieldAccess: (
    target: ProductFieldPermissionTarget
  ) => ProductFieldAccess = () => 'write',
  options?: {
    productId?: string
    source?: ProductSource
  }
): Promise<ProductCreatePayload> {
  const isWritable = (target: ProductFieldPermissionTarget) =>
    getFieldAccess(target) === 'write'
  const picture = isWritable({ field: 'picture' })
    ? await uploadProductImage(
        formValue.picture?.[0],
        options?.productId
      ).catch(tagProductImageUploadError)
    : null
  const propertyValues: ProductCreatePropertyValue[] = []
  const systemProductFields = new Set([
    'supplier',
    'factory',
    'number',
    'name',
    'picture',
  ])

  for (const field of fields) {
    if (field.system || systemProductFields.has(field.field)) {
      continue
    }

    if (
      !isWritable({
        field: field.field,
        propertyId: field.property_id,
      })
    ) {
      continue
    }

    const isUploadListField =
      field.data_type === 'file' ||
      field.data_type === 'image' ||
      field.data_type === 'image_description'
    const fieldValue = isUploadListField
      ? await uploadProductImageList(
          formValue[field.field],
          options?.productId
        ).catch(tagProductImageUploadError)
      : field.data_type === 'date'
      ? toProductDatePayloadValue(formValue[field.field])
      : formValue[field.field] ?? ''

    const includeDescription =
      field.description &&
      isWritable({
        field: field.description.field,
      })

    propertyValues.push({
      field: field.field,
      name: field.name,
      value: fieldValue,
      data_type: field.data_type,
      ...(includeDescription && {
        description: {
          ...field.description,
          value: formValue[field.description.field] ?? '',
        },
      }),
      required: field.required,
      display_order: field.display_order,
    })
  }

  const payload: ProductCreatePayload = {
    status: 'active',
    source: options?.source ?? '',
    property_values: propertyValues,
  }

  if (isWritable({ field: 'picture' })) {
    payload.picture = picture?.id ?? picture?.media_id ?? null
  }

  if (isWritable({ field: 'number' })) {
    payload.number = formValue.number ?? formValue.name ?? ''
  }

  if (isWritable({ field: 'name' })) {
    payload.name = formValue.name ?? ''
  }

  // Supplier is exempt from field permissions and resolves as writable.
  if (isWritable({ field: 'supplier' })) {
    payload.supplier = formValue.supplier || null
  }

  return payload
}

export async function prepareUpdateProductPayload(
  formValue: ProductFormValue,
  fields: ProductDefaultFieldSetting[],
  getFieldAccess: (target: ProductFieldPermissionTarget) => ProductFieldAccess,
  options?: {
    productId?: string
  }
): Promise<ProductUpdatePayload> {
  const isWritable = (target: ProductFieldPermissionTarget) =>
    getFieldAccess(target) === 'write'
  const propertyValues: ProductCreatePropertyValue[] = []

  for (const field of fields) {
    if (
      field.system ||
      ['supplier', 'factory', 'number', 'name', 'picture'].includes(field.field)
    ) {
      continue
    }

    if (
      !isWritable({
        field: field.field,
        propertyId: field.property_id,
      })
    ) {
      continue
    }

    const isUploadListField =
      field.data_type === 'file' ||
      field.data_type === 'image' ||
      field.data_type === 'image_description'
    const fieldValue = isUploadListField
      ? await uploadProductImageList(
          formValue[field.field],
          options?.productId
        ).catch(tagProductImageUploadError)
      : field.data_type === 'date'
      ? toProductDatePayloadValue(formValue[field.field])
      : formValue[field.field] ?? ''
    const includeDescription =
      field.description &&
      isWritable({
        field: field.description.field,
      })

    propertyValues.push({
      field: field.field,
      name: field.name,
      value: fieldValue,
      data_type: field.data_type,
      ...(includeDescription && {
        description: {
          ...field.description,
          value: formValue[field.description.field] ?? '',
        },
      }),
      required: field.required,
      display_order: field.display_order,
    })
  }

  const payload: ProductUpdatePayload = {
    status: 'active',
  }

  if (isWritable({ field: 'picture' })) {
    const picture = await uploadProductImage(
      formValue.picture?.[0],
      options?.productId
    ).catch(tagProductImageUploadError)
    payload.picture = picture?.id ?? picture?.media_id ?? null
  }

  if (isWritable({ field: 'number' })) {
    payload.number = formValue.number ?? ''
  }

  if (isWritable({ field: 'name' })) {
    payload.name = formValue.name ?? ''
  }

  // Supplier is exempt from field permissions and resolves as writable.
  if (isWritable({ field: 'supplier' })) {
    payload.supplier = formValue.supplier || null
  }

  if (propertyValues.length) {
    payload.property_values = propertyValues
  }

  return payload
}

export function transformProductDetailToFormValue(
  product: ProductDetail | null,
  fields: ProductDefaultFieldSetting[]
) {
  if (!product) return {}

  const formValue: ProductFormValue = {
    supplier: product.supplier?.id,
    factory: product.factory?.id,
    number: product.number,
    name: product.name,
  }
  const picture = toProductUploadFile(product.picture)

  if (picture) {
    formValue.picture = [picture]
  }

  const propertyValueMap = new Map(
    product.property_values?.map((propertyValue) => [
      propertyValue.field,
      propertyValue,
    ])
  )
  const systemProductFields = new Set([
    'supplier',
    'factory',
    'number',
    'name',
    'picture',
  ])

  fields.forEach((field) => {
    if (field.system || systemProductFields.has(field.field)) {
      return
    }

    const propertyValue = propertyValueMap.get(field.field)

    if (!propertyValue) {
      formValue[field.field] =
        field.data_type === 'date'
          ? toProductDateFormValue(field.value)
          : field.value || undefined
      return
    }

    if (
      field.data_type === 'file' ||
      field.data_type === 'image' ||
      field.data_type === 'image_description'
    ) {
      formValue[field.field] = parseProductMediaValue(propertyValue.value)
        .map(toProductUploadFile)
        .filter(Boolean)
      if (field.description?.field) {
        formValue[field.description.field] =
          propertyValue.description?.value ?? ''
      }
      return
    }

    if (field.data_type === 'date') {
      formValue[field.field] = toProductDateFormValue(
        getTextPropertyValue(propertyValue)
      )
      return
    }

    formValue[field.field] = getTextPropertyValue(propertyValue)
  })

  return formValue
}

export async function createProduct(payload: ProductCreatePayload) {
  const response = await createProducRef(payload)

  return response.data?.data ?? response.data
}

export async function getProduct(
  params: ProductSearchParams,
  filterPayload: ProductFilterPayload = {}
): Promise<ProductListResult> {
  const response = await getProductRef(params, filterPayload)

  return {
    products: response.data?.data ?? [],
    pagination: response.data?.pagination ?? null,
  }
}

export async function getProductSetting(): Promise<ProductSetting> {
  const response = await getProductSettingRef()
  const setting = response.data?.data?.[0]?.value

  return {
    fieldNames: setting?.field_names ?? [],
    defaultFields: setting?.default_fields ?? [],
    hasManualSource: isProductManualSourceEnabled(setting),
    internalFields: setting?.internal_fields ?? [],
  }
}

export async function getProductSuppliers(
  companyId?: string
): Promise<ProductSupplier[]> {
  const response = await getProductSupplierRef(companyId)

  return response.data?.data ?? []
}

export const getProductSuppliersDropdown = async (
  params: any,
  drawerMode: string = 'myOrders'
) => {
  // TODO: implement next landing page
  const checkMode = {
    myOrders: 'orders',
    myTasks: 'orders',
    myQuality: 'orders',
    myProducts: 'products',
  }
  const path = checkMode[`${drawerMode}`] || 'orders'
  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 14,
    ...(params.search && { search: params.search }),
  }

  const response = await HttpRequest.get(`/${path}/suppliers`, {
    params: queryParams,
  })
  const data = response.data?.data || []
  const meta = response.data?.meta || response.data?.pagination || {}

  return {
    data,
    pagination: {
      total_record: meta.total_record ?? data.length,
      page_size: meta.page_size ?? queryParams.page_size,
      page_number: meta.page_number ?? queryParams.page_number,
      total_page: meta.total_page ?? meta.last_page ?? 1,
      last_page: meta.last_page ?? meta.total_page ?? 1,
    },
  }
}

export const getProductNumbersDropdown = async (params: any) => {
  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 10,
    field: 'number',
    ...(params.search && { search: params.search }),
  }

  const response = await HttpRequest.get('/products/default_field', {
    params: queryParams,
  })
  const data = response.data?.data || []

  return {
    data,
    pagination: response.data?.pagination || {
      total_record: data.length,
      page_size: queryParams.page_size,
      page_number: queryParams.page_number,
      total_page: 1,
    },
  }
}

export const getProductNamesDropdown = async (params: any) => {
  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 10,
    field: 'name',
    ...(params.search && { search: params.search }),
  }

  const response = await HttpRequest.get('/products/default_field', {
    params: queryParams,
  })
  const data = response.data?.data || []

  return {
    data,
    pagination: response.data?.pagination || {
      total_record: data.length,
      page_size: queryParams.page_size,
      page_number: queryParams.page_number,
      total_page: 1,
    },
  }
}

export async function getProductFactories(
  companyId?: string,
  supplierId?: string
): Promise<ProductSupplier[]> {
  const response = await getProductFactoryRef(companyId, supplierId)

  return response.data?.data ?? []
}

export async function getProductDetail(
  productId: string
): Promise<ProductDetail | null> {
  const response = await getProductDetailRef(productId)

  return response.data?.data ?? null
}

export async function updateProduct(
  productId: string,
  payload: ProductUpdatePayload
) {
  const response = await updateProductRef(productId, payload)
  return response.data?.data ?? response.data
}

export async function deleteProduct(productId: string) {
  return await deleteProductRef(productId)
}

export const getProductCustomFieldData = async (params: any) => {
  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 10,
    field: params.field,
    ...(params.search && { search: params.search }),
  }
  const response = await HttpRequest.get(
    '/products/advance_filter_custom_column',
    { params: queryParams }
  )
  if (!response)
    return {
      data: [],
      pagination: {
        total_record: 0,
        page_size: 10,
        page_number: 1,
        total_page: 0,
      },
    }
  return response.data
}

export async function getProductFieldPermission(): Promise<
  ProductFieldPermissionRecord[]
> {
  const response = await getProductFieldPermissionRef()
  const permissions = response.data?.data ?? response.data

  return Array.isArray(permissions) ? permissions : []
}
